/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: XmlPreviewModal.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    XML preview modal component for Logging Settings.
────────────────────────────────────────────────────────────────*/

import { Terminal, FileText, X, Download } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { Button } from '../../../components/ui/button';

interface XmlPreviewModalProps {
  isOpen: boolean;
  type: 'console' | 'file' | null;
  consoleXml?: string;
  fileXml?: string;
  onClose: () => void;
  onTypeChange: (type: 'console' | 'file') => void;
  onDownload: (xml: string, filename: string) => void;
}

export function XmlPreviewModal({
  isOpen,
  type,
  consoleXml,
  fileXml,
  onClose,
  onTypeChange,
  onDownload
}: XmlPreviewModalProps) {
  const { t } = useI18n();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 max-w-4xl max-h-[80vh] w-full mx-4 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">
              {t('loggingSettings.xmlPreview.title')}
            </h3>
            <div className="flex gap-2">
              <Button
                variant={type === 'console' ? 'default' : 'outline'}
                size="sm"
                onClick={() => onTypeChange('console')}
                disabled={!consoleXml}
              >
                <Terminal className="h-4 w-4 mr-2" />
                console.conf.xml
              </Button>
              <Button
                variant={type === 'file' ? 'default' : 'outline'}
                size="sm"
                onClick={() => onTypeChange('file')}
                disabled={!fileXml}
              >
                <FileText className="h-4 w-4 mr-2" />
                logfile.conf.xml
              </Button>
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-auto mb-4">
          {type === 'console' && consoleXml ? (
            <pre className="bg-muted p-4 rounded text-sm overflow-auto font-mono">
              <code>{consoleXml}</code>
            </pre>
          ) : type === 'file' && fileXml ? (
            <pre className="bg-muted p-4 rounded text-sm overflow-auto font-mono">
              <code>{fileXml}</code>
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground italic text-center py-8">
              {t('loggingSettings.xml.noXmlGenerated')}
            </p>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          {type === 'console' && consoleXml && (
            <Button
              onClick={() => onDownload(consoleXml, 'console.conf.xml')}
              size="sm"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('loggingSettings.buttons.downloadConsoleXml')}
            </Button>
          )}
          {type === 'file' && fileXml && (
            <Button
              onClick={() => onDownload(fileXml, 'logfile.conf.xml')}
              size="sm"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('loggingSettings.buttons.downloadFileXml')}
            </Button>
          )}
          <Button
            onClick={onClose}
            variant="outline"
            size="sm"
          >
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}

